import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Close all other <accordion-custom> elements inside the same parent
   */
  #closeSiblings() {
    const parent = this.parentNode;
    if (!parent) return;

    const siblings = parent.querySelectorAll('accordion-custom');
    siblings.forEach((el) => {
      if (el !== this && el instanceof AccordionCustom && el.details.open) {
        el.details.open = false;
      }
    });
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // ─── Close other accordions before this one toggles ───────
    this.#closeSiblings();
    
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
  const isMobile = isMobileBreakpoint();

  let desktopOpen = false;

  if (!isMobile) {
    const hasDesktopDefault = this.hasAttribute('open-by-default-on-desktop');

    // Option 1: Is this the first accordion item in the list?
    const isFirstItem = this.isFirstAccordionItem();

    // Option 2: Is this item "currently selected" (e.g. matches URL hash or has a class)?
    const isCurrentlySelected = this.isCurrentlySelected();

    desktopOpen = hasDesktopDefault && (isFirstItem || isCurrentlySelected);
  }

  this.details.open =
    (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
    desktopOpen;
}

/**
 * Checks if this accordion item is the first one among siblings
 */
isFirstAccordionItem() {
  const parent = this.parentNode;
  if (!parent) return false;

  const accordions = Array.from(parent.querySelectorAll('accordion-custom'));
  return accordions[0] === this;
}


      /**
 * Checks if this item should be considered "currently selected"
 * You can customize this logic based on your needs:
 * Examples:
 * - Matches current URL hash (e.g. #section-1)
 * - Has a specific class like .is-active
 * - Has data-selected attribute
 */
isCurrentlySelected() {
  // Example 1: Match URL hash like href="#about" → opens accordion with id="about"
  const hash = window.location.hash.slice(1);
  if (hash && (this.id === hash || this.details.id === hash)) {
    return true;
  }

  // Example 2: Has .is-active class
  if (this.classList.contains('is-active') || this.details.classList.contains('is-active')) {
    return true;
  }

  // Example 3: Has data-selected attribute
  if (this.hasAttribute('data-selected')) {
    return true;
  }

  return false;
}


  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
